import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { formatDate, formatDuration } from "../format.js";
import { grooveSnapshotHref } from "../grooves.js";
import { PracticeHistory } from "../PracticeHistory.js";
import {
  getStaffCurrentPracticeTodos,
  getStaffPracticeTodoHistory,
  withdrawStaffPracticeTodo,
  type PracticeTodo,
} from "../practiceTodos.js";
import { ConfirmDialog } from "../ui/Dialog.js";
import { useAsync } from "../useAsync.js";
import { StudentResources } from "./StudentResources.js";
import { PracticeTaskMaterials } from "../PracticeTaskMaterials.js";

export function TutorPracticeTodos({ studentId, refreshVersion = 0 }: { studentId: string; refreshVersion?: number }) {
  const currentTodos = useAsync(() => getStaffCurrentPracticeTodos(studentId), [studentId, refreshVersion]);
  const history = useAsync(() => getStaffPracticeTodoHistory(studentId), [studentId]);
  const [historyTodos, setHistoryTodos] = useState<PracticeTodo[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyHasMore, setHistoryHasMore] = useState(false);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyProblem, setHistoryProblem] = useState<string | null>(null);
  const [withdrawing, setWithdrawing] = useState<PracticeTodo | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);

  useEffect(() => {
    if (!history.data) return;
    setHistoryTodos(history.data.todos);
    setHistoryPage(history.data.page);
    setHistoryHasMore(history.data.hasMore);
    setHistoryProblem(null);
  }, [history.data]);

  async function withdraw() {
    if (withdrawing === null || busy) return;
    setBusy(true);
    setProblem(null);
    try {
      await withdrawStaffPracticeTodo(studentId, withdrawing.id);
      setWithdrawing(null);
      currentTodos.reload();
      history.reload();
    } catch (cause: unknown) {
      setProblem(cause instanceof Error ? cause.message : "Could not withdraw that practice to-do.");
    } finally {
      setBusy(false);
    }
  }

  async function loadOlderHistory() {
    if (historyLoading || !historyHasMore) return;
    setHistoryLoading(true);
    setHistoryProblem(null);
    try {
      const next = await getStaffPracticeTodoHistory(studentId, historyPage + 1);
      setHistoryTodos((current) => {
        const known = new Set(current.map((todo) => todo.id));
        return [...current, ...next.todos.filter((todo) => !known.has(todo.id))];
      });
      setHistoryPage(next.page);
      setHistoryHasMore(next.hasMore);
    } catch (cause: unknown) {
      setHistoryProblem(
        cause instanceof Error ? cause.message : "Could not load older practice to-dos.",
      );
    } finally {
      setHistoryLoading(false);
    }
  }

  return (
    <>
      <PracticeHistory endpoint={`/admin/students/${studentId}/practice/history`} variant="staff" />

      <h2>Practice to-dos</h2>
      {currentTodos.loading && !currentTodos.data && history.loading && !history.data && (
        <p className="empty">Loading practice to-dos…</p>
      )}
      {(currentTodos.error || history.error) && (
        <div className="error">We could not load this student&rsquo;s practice to-dos right now.</div>
      )}
      {problem && <div className="error small">{problem}</div>}
      {currentTodos.data && (
        <div className="panel">
          {currentTodos.data.length === 0 ? (
            <p className="small muted" style={{ margin: 0 }}>No current practice to-dos are set.</p>
          ) : (
            <div className="stack" style={{ gap: 10 }}>
              {currentTodos.data.map((todo) => {
                const practiceSeconds = Number.isFinite(todo.practiceSeconds) ? todo.practiceSeconds : 0;
                return (
                  <div
                    key={todo.id}
                    className="spread"
                    style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}
                  >
                    <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                      <strong className={todo.withdrawnAt ? "muted" : undefined}>
                        {todo.title}
                      </strong>
                      {(todo.suggestedMinutes != null || practiceSeconds > 0) && (
                        <div className="small muted" style={{ marginTop: 3 }}>
                          {todo.suggestedMinutes != null && <>Aim for {todo.suggestedMinutes} min/day</>}
                          {todo.suggestedMinutes != null && practiceSeconds > 0 && " · "}
                          {practiceSeconds > 0 && <>Practised {formatDuration(practiceSeconds)}</>}
                        </div>
                      )}
                      {todo.repertoireTitle && (
                        <div className="small muted" style={{ marginTop: 3 }}>
                          Repertoire: {todo.repertoireComposer ? `${todo.repertoireComposer} — ` : ""}
                          {todo.repertoireTitle}
                        </div>
                      )}
                      <PracticeTaskMaterials materials={todo.materials} />
                      {todo.groove && !todo.materials?.some(material => material.groove?.query === todo.groove?.query && material.groove?.title === todo.groove?.title) && (
                        <div className="small" style={{ marginTop: 3 }}>
                          <Link to={grooveSnapshotHref(todo.groove.token)}>
                            Open and play “{todo.groove.title}”
                          </Link>
                        </div>
                      )}
                      {todo.description && (
                        <div className="small muted" style={{ marginTop: 3 }}>{todo.description}</div>
                      )}
                      {todo.completedAt && (
                        <div className="small muted" style={{ marginTop: 3 }}>
                          Completed {formatDate(todo.completedAt)}
                        </div>
                      )}
                    </div>
                    <div className="row" style={{ justifyContent: "flex-end", flexWrap: "wrap" }}>
                      <span className="tag warn">To do</span>
                      <button
                        className="quiet small"
                        type="button"
                        disabled={busy}
                        onClick={() => setWithdrawing(todo)}
                      >
                        Withdraw
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {(history.data?.total ?? 0) > 0 && (
        <details className="ui-disclosure" style={{ marginTop: 14 }}>
          <summary>History</summary>
          <div className="panel">
            <p className="small muted" style={{ marginTop: 0 }}>
              Completed and withdrawn practice stays here as part of the student&rsquo;s record.
            </p>
            <div className="stack" style={{ gap: 10 }}>
              {historyTodos.map((todo) => {
                const practiceSeconds = Number.isFinite(todo.practiceSeconds) ? todo.practiceSeconds : 0;
                return (
                  <div
                    key={todo.id}
                    className="spread"
                    style={{ gap: 12, alignItems: "flex-start", flexWrap: "wrap" }}
                  >
                    <div style={{ flex: "1 1 220px", minWidth: 0 }}>
                      <strong className="muted">{todo.title}</strong>
                      <PracticeTaskMaterials materials={todo.materials} />
                      <div className="small muted" style={{ marginTop: 3 }}>
                        {todo.withdrawnAt ? "No longer assigned" : "Completed"}
                        {todo.completedAt ? ` · ${formatDate(todo.completedAt)}` : ""}
                        {practiceSeconds > 0 ? ` · Practised ${formatDuration(practiceSeconds)}` : ""}
                      </div>
                      {todo.description && (
                        <div className="small muted" style={{ marginTop: 3 }}>{todo.description}</div>
                      )}
                    </div>
                    <span className="tag">{todo.withdrawnAt ? "No longer assigned" : "Done"}</span>
                  </div>
                );
              })}
            </div>
            {historyHasMore && (
              <div className="row" style={{ alignItems: "center", gap: 10, flexWrap: "wrap", marginTop: 12 }}>
                <button
                  className="action-tertiary small"
                  type="button"
                  disabled={historyLoading}
                  onClick={() => void loadOlderHistory()}
                >
                  {historyLoading ? "Loading older…" : "Load older"}
                </button>
                {historyProblem && <span className="error small">{historyProblem}</span>}
              </div>
            )}
          </div>
        </details>
      )}

      <StudentResources studentId={studentId} onPracticeChanged={currentTodos.reload} />

      {withdrawing && (
        <ConfirmDialog
          title="Withdraw this practice to-do?"
          description="It will be removed from current practice. Completed work, notes and practice history will be kept."
          confirmLabel="Withdraw to-do"
          confirmingLabel="Withdrawing…"
          confirmClassName="action-danger"
          busy={busy}
          onConfirm={withdraw}
          onClose={() => setWithdrawing(null)}
        >
          <p><strong>{withdrawing.title}</strong></p>
        </ConfirmDialog>
      )}
    </>
  );
}
